import { useState, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Pencil, Trash2, Search, Upload, FileText } from "lucide-react";
import { format } from "date-fns";
import { sendAcademicNotifications } from "@/lib/sendAcademicNotifications";
import { getSignedFileUrl } from "@/lib/getSignedFileUrl";
import { ClassTargeting, ExpiryControls } from "./AcademicFormControls";

interface DocForm {
  title: string;
  description: string;
  category: string;
  file_url: string;
  published: boolean;
  target_classes: string[];
  expires_at: Date | null;
  expiry_mode: string;
}

const emptyForm: DocForm = {
  title: "", description: "", category: "semester", file_url: "",
  published: true, target_classes: [], expires_at: null, expiry_mode: "none",
};

const CATEGORY_LABELS: Record<string, string> = {
  semester: "Semester", annual: "Annual", block: "Block", special: "Special",
};

const ACCEPTED_FILE_TYPES = ".pdf,.doc,.docx,.xls,.xlsx";

export function AcademicDocumentsManager() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const [form, setForm] = useState<DocForm>(emptyForm);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const { data: documents = [], isLoading } = useQuery({
    queryKey: ["academic-documents"],
    queryFn: async () => {
      const { data, error } = await supabase.from("academic_documents").select("*").order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const { data: availableClasses = [] } = useQuery({
    queryKey: ["available-classes"],
    queryFn: async () => {
      const { data, error } = await supabase.from("courses").select("class").not("class", "is", null);
      if (error) throw error;
      return [...new Set(data.map((c: any) => c.class as string))].sort();
    },
  });

  const uploadFile = async (file: File): Promise<string> => {
    const ext = file.name.split(".").pop();
    const path = `${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
    const { error } = await supabase.storage.from("academic-files").upload(path, file);
    if (error) throw error;
    // Store the path, not a public URL - we'll generate signed URLs when viewing
    return path;
  };

  const saveMutation = useMutation({
    mutationFn: async (data: DocForm & { id?: string; file?: File | null }) => {
      setUploading(true);
      const isNewPublish = data.published && (!data.id || !editing?.published);
      let fileUrl = data.file_url;
      if (data.file) fileUrl = await uploadFile(data.file);
      const payload = {
        title: data.title,
        description: data.description || null,
        category: data.category,
        file_url: fileUrl || null,
        published: data.published,
        author_id: user!.id,
        target_classes: data.target_classes,
        expires_at: data.expires_at ? data.expires_at.toISOString() : null,
      };
      if (data.id) {
        const { error } = await supabase.from("academic_documents").update(payload).eq("id", data.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("academic_documents").insert(payload);
        if (error) throw error;
      }
      if (isNewPublish) {
        await sendAcademicNotifications({
          title: `📋 New timetable: ${data.title}`,
          message: data.description?.slice(0, 200),
          link: "/academic",
          targetClasses: data.target_classes,
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["academic-documents"] });
      toast({ title: editing ? "Timetable updated" : "Timetable uploaded" });
      setFormOpen(false); setEditing(null); setSelectedFile(null); setUploading(false);
    },
    onError: (e: any) => {
      setUploading(false);
      toast({ title: "Error", description: e.message, variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("academic_documents").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["academic-documents"] });
      toast({ title: "Document deleted" });
    },
    onError: (e: any) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const filtered = documents.filter((d: any) =>
    `${d.title} ${d.description || ""}`.toLowerCase().includes(search.toLowerCase())
  );

  const openNew = () => { setEditing(null); setForm(emptyForm); setSelectedFile(null); setFormOpen(true); };
  const openEdit = (d: any) => {
    setEditing(d);
    setForm({
      title: d.title, description: d.description || "", category: d.category,
      file_url: d.file_url || "", published: d.published,
      target_classes: d.target_classes || [],
      expires_at: d.expires_at ? new Date(d.expires_at) : null,
      expiry_mode: d.expires_at ? "date" : "none",
    });
    setSelectedFile(null); setFormOpen(true);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="relative w-full sm:flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search timetables..." className="pl-9" value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <Button size="sm" onClick={openNew}><Plus className="mr-2 h-4 w-4" />Upload Timetable</Button>
      </div>

      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Edit Timetable" : "Upload Timetable"}</DialogTitle></DialogHeader>
          <form onSubmit={e => { e.preventDefault(); saveMutation.mutate({ ...form, id: editing?.id, file: selectedFile }); }} className="space-y-4">
            <div><Label>Title / Name *</Label><Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} required placeholder="e.g. Semester I Timetable 2026" /></div>
            <div><Label>Description</Label><Textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={2} placeholder="Brief description" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Type *</Label>
                <Select value={form.category} onValueChange={v => setForm(f => ({ ...f, category: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="semester">Semester</SelectItem>
                    <SelectItem value="annual">Annual</SelectItem>
                    <SelectItem value="block">Block</SelectItem>
                    <SelectItem value="special">Special</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col gap-2">
                <Label>Upload File</Label>
                <label className="flex items-center gap-2 cursor-pointer border rounded-md px-3 py-2 text-sm hover:bg-muted/50 transition-colors">
                  <Upload className="h-4 w-4 text-muted-foreground" />
                  <span className="truncate">{selectedFile ? selectedFile.name : "Choose file"}</span>
                  <input type="file" accept={ACCEPTED_FILE_TYPES} className="hidden" onChange={e => setSelectedFile(e.target.files?.[0] || null)} />
                </label>
                <p className="text-xs text-muted-foreground">PDF, Word, or Excel</p>
              </div>
            </div>
            {(form.file_url && !selectedFile) && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <FileText className="h-4 w-4" />
                <button type="button" className="underline truncate max-w-[300px]" onClick={async () => {
                  const url = await getSignedFileUrl(form.file_url);
                  if (url) window.open(url, "_blank");
                }}>Current file</button>
              </div>
            )}

            <ClassTargeting
              availableClasses={availableClasses}
              selectedClasses={form.target_classes}
              onChange={classes => setForm(f => ({ ...f, target_classes: classes }))}
            />

            <ExpiryControls
              expiryMode={form.expiry_mode}
              expiresAt={form.expires_at}
              onModeChange={mode => setForm(f => ({ ...f, expiry_mode: mode }))}
              onDateChange={date => setForm(f => ({ ...f, expires_at: date }))}
            />

            <div className="flex items-center gap-2">
              <Switch checked={form.published} onCheckedChange={v => setForm(f => ({ ...f, published: v }))} />
              <Label>{form.published ? "Visible to students" : "Hidden from students"}</Label>
            </div>
            <Button type="submit" disabled={saveMutation.isPending || uploading} className="w-full">
              {uploading ? "Uploading..." : saveMutation.isPending ? "Saving..." : "Save"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <Card>
        <CardHeader>
          <CardTitle>Timetables</CardTitle>
          <CardDescription>{filtered.length} documents</CardDescription>
        </CardHeader>
        <CardContent className="table-responsive">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Title</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Classes</TableHead>
                <TableHead>Visibility</TableHead>
                <TableHead>Expires</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((d: any) => (
                <TableRow key={d.id}>
                  <TableCell className="font-medium max-w-[200px] truncate">{d.title}</TableCell>
                  <TableCell><Badge variant="outline">{CATEGORY_LABELS[d.category] || d.category}</Badge></TableCell>
                  <TableCell className="text-sm max-w-[120px] truncate">
                    {d.target_classes?.length > 0
                      ? d.target_classes.length === availableClasses.length ? "All" : d.target_classes.join(", ")
                      : "—"}
                  </TableCell>
                  <TableCell>
                    <Badge variant={d.published ? "default" : "secondary"}>
                      {d.published ? "Visible" : "Hidden"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm">
                    {d.expires_at ? format(new Date(d.expires_at), "PP") : "—"}
                  </TableCell>
                  <TableCell className="text-sm">{new Date(d.created_at).toLocaleDateString()}</TableCell>
                  <TableCell className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(d)}><Pencil className="h-3 w-3" /></Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild><Button variant="ghost" size="sm"><Trash2 className="h-3 w-3 text-destructive" /></Button></AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader><AlertDialogTitle>Delete document?</AlertDialogTitle><AlertDialogDescription>This action cannot be undone.</AlertDialogDescription></AlertDialogHeader>
                        <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => deleteMutation.mutate(d.id)}>Delete</AlertDialogAction></AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && (
                <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">{isLoading ? "Loading..." : "No timetables found"}</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
